import { useTranslation } from 'react-i18next';
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  ArrowUpDown,
  X,
  Check,
  Users,
  Tag,
  Crown,
  Package,
  ChevronDown,
  ShoppingBasket
} from 'lucide-react';
import { usernameMarketApi } from '@/utils/api';
import { socket } from '@/utils/socket';
import {
  PortalsSearchIcon,
  PortalsActivityIcon,
  PortalsGridIcon,
  PortalsListIcon,
  PortalsChevronUpIcon,
  PortalsFilterIcon,
  PortalsSortIcon
} from './PortalsIcons';
import { GramIcon } from './GramIcon';

export type ViewMode = 'grid' | 'list' | 'compact' | 'activity';

interface CollectionItem {
  id: string;
  name: string;
  badgeCount: string;
  floorPrice: number;
  vol24h: string;
  selected: boolean;
  ownerName?: string;
  features?: string[];
}

export interface FilterOptions {
  sellers: string[];
  maxPrice: number | null;
  name: string | null;
}

export interface NamedFilter {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface ExperimentalFilterBarProps {
  quickFind: string;
  onQuickFindChange: (val: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  onFilterApply?: (selectedIds: string[], opts?: Partial<FilterOptions>) => void;
  items?: any[];
  filters?: NamedFilter[];
  activeFilter?: string;
  onFilterSelect?: (id: string) => void;
  filterTitle?: string;
  hideRoundTools?: boolean;
  hideCollectionButtons?: boolean;
  onPurchasesClick?: () => void;
}

interface CollectionTab {
  id: 'users' | 'types';
  label: string;
  icon: React.ReactNode;
}

const TABS: CollectionTab[] = [
  { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
  { id: 'types', label: 'Types', icon: <Tag className="w-4 h-4" /> },
];

export const ExperimentalFilterBar: React.FC<ExperimentalFilterBarProps> = ({
  quickFind,
  onQuickFindChange,
  viewMode = 'grid',
  onViewModeChange,
  onFilterApply,
  items = [],
  filters,
  activeFilter,
  onFilterSelect,
  filterTitle,
  hideRoundTools,
  hideCollectionButtons,
  onPurchasesClick
}) => {
  const { t } = useTranslation(['common', 'marketplace', 'store', 'profile', 'rules']);
  const [isPriceFilterOpen, setIsPriceFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState(0);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'types'>('users');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [purchaseCount, setPurchaseCount] = useState<number | null>(null);

  useEffect(() => {
    if (!onPurchasesClick) return;
    const load = async () => {
        try {
            const res = await usernameMarketApi.myPurchases();
            if (!res.ok) return;
            const d = await res.json();
            if (d?.success) setPurchaseCount(d.counts?.total ?? 0);
        } catch (e) {}
    };
    load();
    const refresh = () => load();
    socket.on('market_update', refresh);
    socket.on('push_usernames_updated', refresh);
    socket.on('username_listed', refresh);
    return () => {
        socket.off('market_update', refresh);
        socket.off('push_usernames_updated', refresh);
        socket.off('username_listed', refresh);
    };
  }, [onPurchasesClick]);

  // Lock the real scroll container (#root) while the collection sheet is open,
  // so browsing inside the sheet never scrolls the app behind it.
  useEffect(() => {
    if (!isCollectionModalOpen) return;
    const scrollEl = document.getElementById('root') as HTMLElement | null;
    const prev = scrollEl ? scrollEl.style.overflowY : '';
    const prevMain = (document.getElementById('main-scroll-container') as HTMLElement | null)?.style.overflowY ?? '';
    if (scrollEl) scrollEl.style.overflowY = 'hidden';
    const main = document.getElementById('main-scroll-container') as HTMLElement | null;
    if (main) main.style.overflowY = 'hidden';
    return () => {
      if (scrollEl) scrollEl.style.overflowY = prev;
      const main2 = document.getElementById('main-scroll-container') as HTMLElement | null;
      if (main2) main2.style.overflowY = prevMain;
    };
  }, [isCollectionModalOpen]);

  const realItems = useMemo(() => Array.isArray(items) ? items : [], [items]);

  const hasNamedFilters = useMemo(() => !!(filters && filters.length > 0), [filters]);
  const namedFilterActive = hasNamedFilters && !!activeFilter && activeFilter !== 'all';

  const priceBounds = useMemo(() => {
    let min = Infinity;
    let max = 0;
    realItems.forEach(it => {
      const p = Number(it.price || it.current_bid || it.startPrice || 0);
      if (p > 0) {
        if (p < min) min = p;
        if (p > max) max = p;
      }
    });
    if (min === Infinity) min = 0;
    if (max === 0) max = 10000;
    return { min, max };
  }, [realItems]);

  const collections = useMemo<CollectionItem[]>(() => {
    const map = new Map<string, CollectionItem>();
    realItems.forEach(it => {
      const id = String(it.seller_username || it.owner_username || it.owner?.username || it.seller?.username || it.username || 'Unknown');
      const name = String(it.seller_name || it.sellerName || it.ownerName || it.owner_name || it.seller?.first_name || it.owner?.first_name || id);
      const price = Number(it.price || it.current_bid || it.startPrice || 0);

      const existing = map.get(id);
      if (existing) {
        existing.badgeCount = String(Number(existing.badgeCount || 0) + 1);
        existing.floorPrice = existing.floorPrice ? Math.min(existing.floorPrice, price || existing.floorPrice) : (price || existing.floorPrice);
        existing.vol24h = String((Number(existing.badgeCount) * (existing.floorPrice || 0)).toFixed(0));
        if (it.type) {
          if (!existing.features!.includes(String(it.type))) existing.features!.push(String(it.type));
        }
      } else {
        map.set(id, {
          id,
          name,
          badgeCount: '1',
          floorPrice: price || 0,
          vol24h: price ? String(price) : '0',
          selected: selectedIds.includes(id),
          ownerName: name,
          features: it.type ? [String(it.type)] : []
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (b.floorPrice || 0) - (a.floorPrice || 0));
  }, [realItems, selectedIds]);

  const typeCollections = useMemo<CollectionItem[]>(() => {
    const map = new Map<string, CollectionItem>();
    realItems.forEach(it => {
      const type = String(it.type || it.sectionId || 'HOME');
      const price = Number(it.price || it.current_bid || it.startPrice || 0);
      const existing = map.get(type);
      if (existing) {
        existing.badgeCount = String(Number(existing.badgeCount) + 1);
      } else {
        map.set(type, {
          id: type,
          name: type,
          badgeCount: '1',
          floorPrice: price || 0,
          vol24h: price ? String(price) : '0',
          selected: selectedIds.includes(type)
        });
      }
    });
    return Array.from(map.values());
  }, [realItems, selectedIds]);

  const activeCollections = activeTab === 'users' ? collections : typeCollections;

  const isFiltered = selectedIds.length > 0 || !!quickFind.trim() || (isPriceFilterOpen && priceRange > 0);

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const ids = activeCollections.map(c => c.id);
    setSelectedIds(prev => {
      const all = Array.from(new Set([...prev, ...ids]));
      return all;
    });
  };

  const clearAll = () => {
    setSelectedIds([]);
  };

  const applyFilters = () => {
    setIsCollectionModalOpen(false);
    if (onFilterApply) {
      onFilterApply(selectedIds, {
        maxPrice: isPriceFilterOpen && priceRange > 0 ? priceRange : null,
        name: quickFind.trim() || null
      });
    }
  };

  const handleViewModeToggle = (mode: ViewMode) => {
    if (onViewModeChange) onViewModeChange(mode);
  };

  const filteredModalCollections = useMemo(() => {
    const q = modalSearchQuery.trim().toLowerCase();
    if (!q) return activeCollections;
    return activeCollections.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q) ||
      (c.features ?? []).some(f => f.toLowerCase().includes(q))
    );
  }, [activeCollections, modalSearchQuery]);

  const showResultsLabel = `${isFiltered ? selectedIds.length : activeCollections.length} {t('common:selected', 'selected')}`;

  return (
    <div className="px-4 pt-1 pb-2 space-y-2.5">
      <style>{`
        .pfk-glass {
          background-color: rgba(28,28,30,.55);
          -webkit-backdrop-filter: blur(8px) saturate(140%);
          backdrop-filter: blur(8px) saturate(140%);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.05), inset 1.8px 3px 0px -2px rgba(255,255,255,.27), inset -2px -2px 0px -2px rgba(255,255,255,.22), inset -3px -8px 1px -6px rgba(255,255,255,.16), inset -.3px -1px 4px 0px rgba(0,0,0,.24), inset -1.5px 2.5px 0px -2px rgba(0,0,0,.4), inset 0px 3px 4px -2px rgba(0,0,0,.4), 0px 1px 5px 0px rgba(0,0,0,.2), 0px 6px 16px 0px rgba(0,0,0,.16);
          border: none;
        }
        .pfk-input {
          background-color: rgba(28,28,30,.55);
          -webkit-backdrop-filter: blur(8px) saturate(140%);
          backdrop-filter: blur(8px) saturate(140%);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.06), inset 1.8px 3px 0px -2px rgba(255,255,255,.25), inset -2px -2px 0px -2px rgba(255,255,255,.2), 0px 1px 5px 0px rgba(0,0,0,.2), 0px 6px 16px 0px rgba(0,0,0,.16);
          transition: box-shadow .2s;
        }
        .pfk-input:focus-within { box-shadow: inset 0 0 0 1px rgba(51,144,236,.55), inset 1.8px 3px 0px -2px rgba(255,255,255,.25), 0px 1px 5px 0px rgba(0,0,0,.2), 0px 6px 16px 0px rgba(0,0,0,.16); }
        .pfk-pill {
          background-color: rgba(28,28,30,.55);
          -webkit-backdrop-filter: blur(8px) saturate(140%);
          backdrop-filter: blur(8px) saturate(140%);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.05), inset 1.8px 3px 0px -2px rgba(255,255,255,.27), inset -2px -2px 0px -2px rgba(255,255,255,.22), 0px 1px 5px 0px rgba(0,0,0,.2), 0px 6px 16px 0px rgba(0,0,0,.16);
        }
        .fp-fade { animation: fpFade .18s ease-out }
        .fp-sheet { animation: fpUp .32s cubic-bezier(.32,.72,.24,.9) }
        @keyframes fpFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fpUp { from { transform: translateY(80px); opacity: .5 } to { transform: translateY(0); opacity: 1 } }
      `}</style>

      {/* Top Search & Glass Action Buttons Row */}
      <div className="flex items-center gap-2 group">
        <div className="pfk-input flex-1 rounded-full pl-[38px] pr-3 h-10 flex items-center relative transition-all duration-300">
          <PortalsSearchIcon className="w-4 h-4 text-white/40 absolute left-3" />
          <input
            type="text"
            placeholder={t('common:quickFind', 'Quick find')}
            value={quickFind}
            onChange={(e) => onQuickFindChange(e.target.value)}
            className="bg-transparent text-sm text-white placeholder-white/35 font-semibold outline-none w-full"
          />
          {quickFind.trim() && (
            <button
              onClick={() => onQuickFindChange('')}
              className="p-1 -mr-1 rounded-full text-white/40 hover:text-white active:scale-90 transition-all shrink-0 z-10"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0 transition-all duration-300 group-focus-within:pointer-events-none group-focus-within:w-0 group-focus-within:opacity-0 group-focus-within:gap-0">
          {onPurchasesClick && (
            <button
              onClick={onPurchasesClick}
              className="w-10 h-10 pfk-glass rounded-full flex items-center justify-center transition-all active:scale-95 shrink-0 text-white hover:text-white relative"
              title="My Purchases"
            >
              <ShoppingBasket size={18} className="text-white" />
              {purchaseCount != null && purchaseCount > 0 && (
                  <span className="pfk-glass text-white font-bold text-[10px] leading-[11px] min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 box-content z-10">
                      {purchaseCount > 99 ? '99+' : purchaseCount}
                  </span>
              )}
            </button>
          )}

        {/* Button 1: Activity Feed Mode Toggle */}
        {!hideRoundTools && (<>
        <button
          onClick={() => handleViewModeToggle(viewMode === 'activity' ? 'grid' : 'activity')}
          className={`w-10 h-10 pfk-glass rounded-full flex items-center justify-center transition-all active:scale-95 shrink-0 ${
            viewMode === 'activity' ? 'text-white active:scale-95' : 'text-white/50 hover:text-white active:scale-95'
          }`}
          title="Activity log view"
        >
          <PortalsActivityIcon className="w-4 h-4" />
        </button>

        {/* Button 2: Compact List vs Grid Layout Toggle */}
        <button
          onClick={() => handleViewModeToggle(viewMode === 'compact' ? 'grid' : 'compact')}
          className={`w-10 h-10 pfk-glass rounded-full flex items-center justify-center transition-all active:scale-95 shrink-0 ${
            viewMode === 'compact' ? 'text-white active:scale-95' : 'text-white/50 hover:text-white active:scale-95'
          }`}
          title="Switch grid or compact list view"
        >
          {viewMode === 'compact' ? <PortalsListIcon className="w-4 h-4" /> : <PortalsGridIcon className="w-4 h-4" />}
        </button>

{/* Button 3: Expand/Collapse Price Slider Drawer */}
        <button
          onClick={() => setIsPriceFilterOpen(!isPriceFilterOpen)}
          className={`w-10 h-10 pfk-glass rounded-full flex items-center justify-center transition-all active:scale-95 shrink-0 ${
            isPriceFilterOpen ? 'text-white active:scale-95' : 'text-white/50 hover:text-white active:scale-95'
          }`}
          title="Price range filter"
        >
          <PortalsChevronUpIcon className={`w-4 h-4 transition-transform ${isPriceFilterOpen ? '' : 'rotate-180'}`} />
        </button>
        </>)}
        </div>
      </div>

      {/* Expandable Price Range Drawer (bounded by real data) */}
      {isPriceFilterOpen && (
        <div className="pfk-glass rounded-2xl px-4 py-3 flex flex-col gap-3 animate-fade-in">
          <div className="grid grid-cols-2 gap-3">
            <div className="pfk-input rounded-full h-11 flex items-center px-3">
              <input
                type="text"
                inputMode="decimal"
                defaultValue={priceBounds.min === Infinity ? 0 : priceBounds.min}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const v = Number(e.target.value.replace(/[^\d]/g, ''));
                  if (!isNaN(v) && v >= 0) setPriceRange(Math.min(v, priceBounds.max));
                }}
                className="bg-transparent text-sm text-white placeholder-white/35 font-semibold outline-none w-full min-w-0"
              />
              <GramIcon className="w-4 h-4 text-white/40 shrink-0 ml-1" />
            </div>
            <div className="pfk-input rounded-full h-11 flex items-center px-3">
              <GramIcon className="w-4 h-4 text-white/40 shrink-0 mr-1" />
              <input
                type="text"
                inputMode="decimal"
                value={priceRange || priceBounds.max}
                onChange={(e) => {
                  const v = Number(e.target.value.replace(/[^\d]/g, ''));
                  if (!isNaN(v) && v >= 0) setPriceRange(Math.min(v, priceBounds.max));
                }}
                className="bg-transparent text-sm text-white placeholder-white/35 font-semibold outline-none w-full min-w-0 text-right"
              />
            </div>
          </div>

          <div className="relative h-6 flex items-center">
            <div className="absolute inset-x-0 h-2.5 bg-[#3a3a3a] rounded-full" />
            <div
              className="absolute left-0 h-2.5 rounded-full bg-white/40"
              style={{
                width: `${Math.max(0, Math.min(100, ((priceRange || priceBounds.max) - priceBounds.min) / Math.max(1, priceBounds.max - priceBounds.min) * 100))}%`,
              }}
            />
            <div
              className="absolute w-6 h-6 bg-white border-2 rounded-full pointer-events-none"
              style={{
                borderColor: '#282727',
                left: `calc(${Math.max(0, Math.min(100, ((priceRange || priceBounds.max) - priceBounds.min) / Math.max(1, priceBounds.max - priceBounds.min) * 100))}% - 12px)`,
              }}
            />
            <input
              type="range"
              min={priceBounds.min}
              max={Math.max(priceBounds.max, priceBounds.min + 1)}
              value={priceRange || priceBounds.max}
              onChange={(e) => setPriceRange(Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
            />
          </div>
        </div>
      )}

{/* Horizontally Scrollable Glass Filter Pills with Icon Controls */}
        <div className="flex items-center relative py-1 -mr-4 overflow-x-clip">
          {/* Fixed Left Section (Filter, Sort) - STAYS STATIC */}
          {!hideCollectionButtons && (
          <div className="flex items-center gap-1.5 shrink-0 z-10 pr-1.5">
            <button
              onClick={() => { setActiveTab('users'); setIsCollectionModalOpen(true); }}
              className="relative pfk-glass rounded-full px-3 py-[9px] flex items-center justify-center text-white/60 hover:text-white transition-all active:scale-95"
              title={filterTitle || t('marketplace:filterUsersTypes', 'Filter by users / types')}
            >
              <PortalsFilterIcon className="w-4 h-4" />
              <span className="pfk-glass text-white font-bold text-[10px] leading-[11px] w-4 h-[13px] rounded-full flex items-center justify-center absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 box-content">
                {selectedIds.length > 0 ? selectedIds.length : (realItems.length > 0 ? '·' : 0)}
              </span>
            </button>

            <button
              onClick={() => { setActiveTab('types'); setIsCollectionModalOpen(true); }}
              className="pfk-glass rounded-full px-3 py-[9px] flex items-center justify-center text-white/60 hover:text-white transition-all active:scale-95"
              title={t('marketplace:filterItemType', 'Filter by item type')}
            >
              <PortalsSortIcon className="w-4 h-4" />
            </button>

            <div className="w-1 shrink-0" />
          </div>
          )}

          {/* Scrollable Horizontal Glass Pills Container */}
          {filters && filters.length > 0 ? (
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1 flex-1 after:content-[''] after:w-4 after:shrink-0 after:block">
              {(namedFilterActive || isFiltered) && (
                <button
                  onClick={() => {
                    if (namedFilterActive && onFilterSelect) onFilterSelect('all');
                    else clearAll();
                  }}
                  className="w-10 h-10 pfk-glass rounded-full text-purple-300 hover:text-purple-200 transition-all active:scale-95 shrink-0 flex items-center justify-center"
                  title={t('common:clearFilter', 'Clear active filter')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {filters.map((f) => {
                const isOn = activeFilter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => {
                      if (onFilterSelect) onFilterSelect(isOn && activeFilter === f.id ? 'all' : f.id);
                    }}
                    className={`pfk-pill px-3.5 h-10 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 active:scale-95 transition-all ${
                      isOn
                        ? 'text-white bg-white/15 shadow-lg shadow-black/30'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    {f.icon}
                    <span>{f.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1 flex-1 after:content-[''] after:w-4 after:shrink-0 after:block">
            {isFiltered && (
              <button
                onClick={clearAll}
                className="w-10 h-10 pfk-glass rounded-full text-blue-400 hover:text-blue-300 transition-all active:scale-95 shrink-0 flex items-center justify-center"
                title={t('common:clearFilter', 'Clear active filter')}
              >
                <X className="w-4 h-4" />
              </button>
            )}

            <button
              onClick={() => { setActiveTab('users'); setIsCollectionModalOpen(true); }}
              className="pfk-pill px-3.5 h-10 rounded-full text-xs font-bold text-white/80 flex items-center gap-1.5 shrink-0 active:scale-95 transition-all"
            >
              <Users className="w-3.5 h-3.5 text-white/50" />
              <span>{t('common:users', 'Users')}</span>
              <span className="bg-white/10 text-white/80 rounded-full px-1.5 text-[10px] font-bold">
                {collections.length}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-white/40" />
            </button>

            <button
              onClick={() => { setActiveTab('types'); setIsCollectionModalOpen(true); }}
              className="pfk-pill px-3.5 h-10 rounded-full text-xs font-bold text-white/80 flex items-center gap-1.5 border border-white/5 shrink-0 active:scale-95 transition-all"
            >
              <Tag className="w-3.5 h-3.5 text-white/50" />
              <span>{t('common:types', 'Types')}</span>
              <span className="bg-white/10 text-white/80 rounded-full px-1.5 text-[10px] font-bold">
                {typeCollections.length}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-white/40" />
            </button>

            <button
              onClick={() => { setActiveTab('users'); setIsCollectionModalOpen(true); }}
              className="pfk-pill px-3.5 h-10 rounded-full text-xs font-bold text-white/80 flex items-center gap-1 border border-white/5 shrink-0 active:scale-95 transition-all"
            >
              <Crown className="w-3.5 h-3.5 text-white/50" />
              <span>{t('marketplace:topSellers', 'Top Sellers')}</span>
              <ChevronDown className="w-3.5 h-3.5 text-white/40" />
            </button>
          </div>
          )}
        </div>

      {/* Collection Modal Bottom Sheet — portal to <body> so it sits above the bottom nav */}
      {isCollectionModalOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm fp-fade"
            onClick={() => setIsCollectionModalOpen(false)}
          />

          <div className="relative w-full max-w-md glass-strong rounded-t-[32px] border-t border-white/10 p-4 pb-6 flex flex-col max-h-[85vh] shadow-2xl z-10 fp-sheet overscroll-contain">
            <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-black text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {activeTab === 'users' ? <PortalsFilterIcon className="w-5 h-5 text-purple-300" /> : <Tag className="w-5 h-5 text-purple-300" />}
                <span>{activeTab === 'users' ? t('common:users', 'Users') : t('common:types', 'Types')}</span>
                {realItems.length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono">
                    {realItems.length} live items
                  </span>
                )}
              </h3>
              <button
                onClick={() => setIsCollectionModalOpen(false)}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/50 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-3 p-1 pfk-glass rounded-full">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-black shadow-sm'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  <span>{t('common:' + tab.id, tab.label)}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 pfk-input rounded-full pl-[38px] pr-3 h-10 relative flex items-center">
                <PortalsSearchIcon className="w-4 h-4 text-white/40 absolute left-3" />
                <input
                  type="text"
                  placeholder={t('common:searchPlaceholder', 'Search by name, @username or feature...')}
                  value={modalSearchQuery}
                  onChange={(e) => setModalSearchQuery(e.target.value)}
                  className="bg-transparent text-sm text-white placeholder-white/35 font-semibold outline-none w-full pr-7"
                />
                {modalSearchQuery.trim() && (
                  <button
                    onClick={() => setModalSearchQuery('')}
                    className="p-1 -mr-1.5 rounded-full absolute right-2 text-white/40 hover:text-white active:scale-90 transition-all"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button className="w-10 h-10 pfk-glass rounded-full flex items-center justify-center text-white/50 hover:text-white transition-colors">
                <PortalsSortIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center justify-between px-1 py-1.5 text-xs font-semibold text-white/40 border-b border-white/10">
              <button className="flex items-center gap-1 hover:text-white transition-colors">
                <span>{activeTab === 'users' ? t('common:user', 'User') : t('common:type', 'Type')}</span>
                <ArrowUpDown className="w-3 h-3" />
              </button>
              <div className="flex items-center gap-3">
                <span>{t('common:items', 'Items')}</span>
                <span>{t('common:price', 'Price')}</span>
              </div>
            </div>

            <div className="overflow-y-auto no-scrollbar flex-1 divide-y divide-white/10 my-1 pr-1 overscroll-contain">
              {filteredModalCollections.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-white/40">
                  <Package className="w-8 h-8 mb-2 opacity-50" />
                  <span className="text-sm">{t('common:noLiveData', 'No live data yet')}</span>
                </div>
              ) : (
                filteredModalCollections.map((col) => (
                  <div
                    key={col.id}
                    onClick={() => toggleSelection(col.id)}
                    className="flex items-center justify-between py-2.5 px-1 hover:bg-white/[0.06] rounded-xl cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          col.selected
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                            : 'border-2 border-white/25 bg-transparent'
                        }`}
                      >
                        {col.selected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                      </div>

                      <div className="ml-1">
                        <span className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors flex items-center gap-1.5">
                          {col.name}
                          {col.selected && <Check className="w-3 h-3 text-purple-300" />}
                        </span>
                        <span className="block text-[11px] font-mono text-white/40 group-hover:text-white/60">
                          {col.id}
                        </span>
                        {col.features && col.features.length > 0 && (
                          <span className="flex items-center gap-1 mt-0.5 flex-wrap">
                            {col.features.map((f, i) => (
                              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-200 border border-purple-500/20 uppercase">
                                {f}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-sm font-extrabold text-white">
                        {col.floorPrice ? `${col.floorPrice.toLocaleString()} GRAM` : 'n/a'}
                      </div>
                      <div className="text-[11px] font-medium text-white/40">
                        {col.badgeCount} items
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="pt-3 flex items-center gap-3 border-t border-white/10">
              <button
                onClick={clearAll}
                className="flex-1 py-3 glass-inset text-white/80 rounded-2xl font-bold text-sm transition-all active:scale-95"
              >{t('common:clearAll', 'Clear all')}</button>
              <button
                onClick={applyFilters}
                className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-sm shadow-lg shadow-purple-500/25 transition-all active:scale-95"
              >{t('common:showResults', 'Show results')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};